import dayjs from "dayjs";
import {
  type AnnualWheelEntry,
  type AnnualWheelEvent,
  type AnnualWheelRelease,
  type AnnualWheelWorkflowStep,
} from "./types";

export type TimeDomain = [Date, Date];

interface ReviewPeriod {
  reviewFrom: Date;
  reviewTo: Date;
}

// The review of one release of a document; earlier releases were reviewed in earlier years
export interface DocumentReview extends ReviewPeriod {
  entry: AnnualWheelEntry;
  release: AnnualWheelRelease;
}

// A stretch of one document's review; the one after the last deadline has no step
export interface ReviewLeg {
  from: Date;
  to: Date;
  workflowStep?: AnnualWheelWorkflowStep;
}

// Documents that start their review on the same day; reviewTo is the latest end
export interface ReviewWindow extends ReviewPeriod {
  documentReviews: DocumentReview[];
  lane: number;
}

// Where the pin of an event stands, and where its head ends up, along the same axis
export interface EventPinPlace {
  annualWheelEvent: AnnualWheelEvent;
  tip: number;
  head: number;
}

const DATE_FORMATS = {
  date: "D MMM YYYY",
  isoDate: "YYYY-MM-DD",
  dayMonth: "D MMM",
  month: "MMM",
  monthYear: "MMM YYYY",
  year: "YYYY",
};

export type DateFormat = keyof typeof DATE_FORMATS;

export const formatDate = (date: Date | string, format: DateFormat = "date") =>
  dayjs(date).format(DATE_FORMATS[format]);

const startOfDay = (date: string) => dayjs(date).startOf("day").toDate();

export const lerp = (from: number, to: number, t: number) =>
  from + (to - from) * t;

// Labels of the two zoom levels take turns, so they never show on top of each other
export const yearLabelOpacity = (zoom: number) => Math.max(0, 1 - 2 * zoom);

export const documentLabelOpacity = (zoom: number) => Math.max(0, 2 * zoom - 1);

export const yearDomain = (year: number): TimeDomain => [
  new Date(year, 0, 1),
  new Date(year + 1, 0, 1),
];

const dayAfter = (date: Date) => dayjs(date).add(1, "day").toDate();

// A review window runs through the whole of its last day
export const endOfReviewWindow = (reviewTo: Date) => dayAfter(reviewTo);

export const reviewPeriodDomain = ({
  reviewFrom,
  reviewTo,
}: ReviewPeriod): TimeDomain => [reviewFrom, endOfReviewWindow(reviewTo)];

export const interpolateDomain = (
  from: TimeDomain,
  to: TimeDomain,
  t: number,
): TimeDomain => [
  new Date(lerp(from[0].getTime(), to[0].getTime(), t)),
  new Date(lerp(from[1].getTime(), to[1].getTime(), t)),
];

// Maps a date in the domain onto the range, holding dates outside at its ends
export const timeScale =
  ([start, end]: TimeDomain, [from, to]: [number, number]) =>
  (date: Date) => {
    const share =
      (date.getTime() - start.getTime()) / (end.getTime() - start.getTime());

    return lerp(from, to, Math.min(Math.max(share, 0), 1));
  };

export const shiftYears = (
  [start, end]: TimeDomain,
  years: number,
): TimeDomain => [
  dayjs(start).add(years, "year").toDate(),
  dayjs(end).add(years, "year").toDate(),
];

// The wheel shows the year in the middle of what the timeline shows
export const horizonYear = ([start, end]: TimeDomain) =>
  new Date((start.getTime() + end.getTime()) / 2).getFullYear();

export const isInDomain = (date: Date, [start, end]: TimeDomain) =>
  start <= date && date < end;

// Ticks a whole number of weeks apart, counted from the start of the domain
export const weekTicks = ([start, end]: TimeDomain, maxTicks: number) => {
  const weeks = dayjs(end).diff(start, "week", true);
  const weeksPerTick = Math.max(1, Math.ceil(weeks / maxTicks));
  const ticks: Date[] = [];

  for (
    let tick = dayjs(start);
    tick.isBefore(end);
    tick = tick.add(weeksPerTick, "week")
  ) {
    ticks.push(tick.toDate());
  }

  return ticks;
};

// The timeline shows several years, so a period names its year, or both across a year end
export const formatReviewPeriod = ({ reviewFrom, reviewTo }: ReviewPeriod) => {
  const isWithinYear = reviewFrom.getFullYear() === reviewTo.getFullYear();

  return `${formatDate(reviewFrom, isWithinYear ? "dayMonth" : "date")} – ${formatDate(reviewTo, "date")}`;
};

export const reviewWindowStatuses = ["done", "ongoing", "upcoming"] as const;
export type ReviewWindowStatus = (typeof reviewWindowStatuses)[number];

// By the calendar alone: a window is done once its last day is over, published or not
export const reviewWindowStatusOf = (
  { reviewFrom, reviewTo }: ReviewPeriod,
  date: Date,
): ReviewWindowStatus => {
  if (date < reviewFrom) return "upcoming";

  return date < endOfReviewWindow(reviewTo) ? "ongoing" : "done";
};

// One leg per workflow step, each running from the previous deadline through its own due day
export const reviewLegs = ({
  release,
  reviewFrom,
  reviewTo,
}: DocumentReview): ReviewLeg[] => {
  const end = endOfReviewWindow(reviewTo);
  let from = reviewFrom;

  const legs: ReviewLeg[] = release.workflowSteps.map((workflowStep) => {
    const dueDayEnd = dayAfter(startOfDay(workflowStep.dueAt));
    const to = new Date(
      Math.min(Math.max(dueDayEnd.getTime(), from.getTime()), end.getTime()),
    );
    const leg = { from, to, workflowStep };
    from = to;

    return leg;
  });

  return from < end ? [...legs, { from, to: end }] : legs;
};

// One name per step position; undefined where the documents name that step differently
export const sharedWorkflowStepNames = (documentReviews: DocumentReview[]) => {
  const stepCount = Math.max(
    0,
    ...documentReviews.map(({ release }) => release.workflowSteps.length),
  );

  return Array.from({ length: stepCount }, (_, order) => {
    const names = new Set(
      documentReviews.flatMap(
        ({ release }) => release.workflowSteps[order]?.name ?? [],
      ),
    );

    return names.size === 1 ? [...names][0] : undefined;
  });
};

const toDocumentReview = (
  entry: AnnualWheelEntry,
  release: AnnualWheelRelease,
) => {
  const { reviewFrom, reviewTo } = release;
  if (!reviewFrom || !reviewTo) return [];

  const documentReview: DocumentReview = {
    entry,
    release,
    reviewFrom: startOfDay(reviewFrom),
    reviewTo: startOfDay(reviewTo),
  };

  return documentReview.reviewTo < documentReview.reviewFrom
    ? []
    : [documentReview];
};

// One review per release, leaving out releases without a usable review period
export const toDocumentReviews = (entries: AnnualWheelEntry[]) =>
  entries.flatMap((entry) =>
    [entry.latestRelease, ...entry.previousReleases].flatMap((release) =>
      toDocumentReview(entry, release),
    ),
  );

// Review windows that overlap get a lane each; expects them ordered by start
export const toLanes = (
  reviewWindows: Omit<ReviewWindow, "lane">[],
): ReviewWindow[] => {
  const laneEnds: Date[] = [];

  return reviewWindows.map((reviewWindow) => {
    const freeLane = laneEnds.findIndex((end) => end < reviewWindow.reviewFrom);
    const lane = freeLane === -1 ? laneEnds.length : freeLane;
    laneEnds[lane] = reviewWindow.reviewTo;

    return { ...reviewWindow, lane };
  });
};

export const groupByReviewFrom = (
  documentReviews: DocumentReview[],
): ReviewWindow[] => {
  const byReviewFrom = new Map<number, Omit<ReviewWindow, "lane">>();

  for (const documentReview of documentReviews) {
    const { reviewFrom, reviewTo } = documentReview;
    const reviewWindow = byReviewFrom.get(reviewFrom.getTime());

    if (!reviewWindow) {
      byReviewFrom.set(reviewFrom.getTime(), {
        reviewFrom,
        reviewTo,
        documentReviews: [documentReview],
      });
      continue;
    }

    reviewWindow.documentReviews.push(documentReview);
    if (reviewTo > reviewWindow.reviewTo) reviewWindow.reviewTo = reviewTo;
  }

  const sorted = [...byReviewFrom.values()].sort(
    (a, b) => a.reviewFrom.getTime() - b.reviewFrom.getTime(),
  );

  return toLanes(
    sorted.map((reviewWindow) => ({
      ...reviewWindow,
      documentReviews: reviewWindow.documentReviews.sort(
        (a, b) =>
          a.reviewTo.getTime() - b.reviewTo.getTime() ||
          a.entry.name.localeCompare(b.entry.name),
      ),
    })),
  );
};

// An event takes the whole of its day, so its pin stands on the middle of it
export const eventMidday = ({ date }: AnnualWheelEvent) =>
  dayjs(date).hour(12).toDate();

export const byDayThenName = (a: AnnualWheelEvent, b: AnnualWheelEvent) =>
  a.date.localeCompare(b.date) || a.name.localeCompare(b.name);

export const eventsInDomain = (
  events: AnnualWheelEvent[],
  domain: TimeDomain,
) =>
  events
    .filter((annualWheelEvent) =>
      isInDomain(eventMidday(annualWheelEvent), domain),
    )
    .sort(byDayThenName);

// Positions closer together than the gap move apart around their shared middle;
// expects them in order
export const spreadApart = (positions: number[], gap: number) => {
  const clusters: { middle: number; size: number }[] = [];

  for (const position of positions) {
    let cluster = { middle: position, size: 1 };
    let previous = clusters.at(-1);

    // A cluster that takes in a neighbour grows, and can then reach the one before
    while (
      previous &&
      previous.middle + (previous.size * gap) / 2 >
        cluster.middle - (cluster.size * gap) / 2
    ) {
      const size = previous.size + cluster.size;

      clusters.pop();
      cluster = {
        middle:
          (previous.middle * previous.size + cluster.middle * cluster.size) /
          size,
        size,
      };
      previous = clusters.at(-1);
    }

    clusters.push(cluster);
  }

  return clusters.flatMap(({ middle, size }) =>
    Array.from(
      { length: size },
      (_, index) => middle + (index - (size - 1) / 2) * gap,
    ),
  );
};

// Every pin stands on its own day; the heads of pins on nearby days lean apart.
// A crowd of pins overlaps instead of leaning further than maxLean
export const placeEventPins = (
  events: AnnualWheelEvent[],
  domain: TimeDomain,
  position: (date: Date) => number,
  headGap: number,
  maxLean: number,
): EventPinPlace[] => {
  const tips = eventsInDomain(events, domain).map((annualWheelEvent) => ({
    annualWheelEvent,
    tip: position(eventMidday(annualWheelEvent)),
  }));
  const heads = spreadApart(
    tips.map(({ tip }) => tip),
    headGap,
  );

  return tips.map((pin, index) => ({
    ...pin,
    head: Math.min(
      Math.max(heads[index] ?? pin.tip, pin.tip - maxLean),
      pin.tip + maxLean,
    ),
  }));
};

export const shortenLabel = (label: string, maxLength: number) =>
  label.length > maxLength
    ? `${label.slice(0, maxLength - 1).trimEnd()}…`
    : label;
